import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SharedModule } from '../../@commons/ui-components/shared.module';
import { StorageService } from '../../@commons/@services/front/storage.service';
import { BedItemComponent } from '../@components/bed-item/bed-item.component';
import { ZoneCreateWizardComponent } from '../zone-create-wizard/zone-create-wizard.component';
import { UserAdressLocationFormComponent } from '../../user-profile/@components/user-address-card/@toasts/edit-user-adress-toast/user-adress-location-form/user-adress-location-form.component';
import { ConfirmDialogComponent } from '../../@commons/ui-components/confirm-dialog/confirm-dialog.component';
import { CultivationItemViewerComponent } from '../@components/hierarchy-item/cultivation-item-viewer/cultivation-item-viewer.component';
import { SkeletonLoaderComponent } from '../../@commons/@components/loaders/skeleton-loader/skeleton-loader.component';
import { LocationApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/location-api';
import { GardenBedApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/garden-bed/garden-bed-api';
import { PlantApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/plant/plant-api';
import { CultivationHierarchyApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-hierarchy/cultivation-hierarchy-api';
import { LocationListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/location-list.response';
import { GardenBedListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/garden-bed-list.response';
import { PlantListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/plant-list.response';
import { GardenBedType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/garden-bed-type.enum';

interface BedView {
  bed: GardenBedListResponse;
  cultures: any[];
  count: number;
  /** Nombre total de plants (Σ nbPlants des cohortes) — base de la capacité. */
  plantCount: number;
  freeCells: number;
  /** Positions réellement occupées (x = colonne, y = ligne) du mini-aperçu. */
  occupied: { x: number; y: number }[];
  /** Au moins une plante attend une action (étape manuelle dont la durée est écoulée). */
  actionNeeded: boolean;
}

/** Cultures hors planche regroupées par culture (comme l'accueil) : une vignette + un compteur. */
interface CultureGroup {
  culture: any;
  cultivationUid?: string;
  count: number;
  plants: PlantListResponse[];
  /** Au moins une plante du groupe attend une action. */
  actionNeeded: boolean;
}

interface ZoneView {
  zone: LocationListResponse;
  beds: BedView[];
  looseCultures: CultureGroup[];
}

@Component({
  selector: 'vw-garden-overview',
  imports: [
    SharedModule,
    CommonModule,
    TranslateModule,
    BedItemComponent,
    CultivationItemViewerComponent,
    SkeletonLoaderComponent,
    ZoneCreateWizardComponent,
    UserAdressLocationFormComponent,
    ConfirmDialogComponent,
  ],
  templateUrl: './garden-overview.component.html',
  styleUrl: './garden-overview.component.scss',
  providers: [
    { provide: LocationApi, useClass: LocationApi },
    { provide: GardenBedApi, useClass: GardenBedApi },
    { provide: PlantApi, useClass: PlantApi },
    { provide: CultivationHierarchyApi, useClass: CultivationHierarchyApi },
  ],
})
export class GardenOverviewComponent implements OnInit {
  isLoading = true;
  zones: ZoneView[] = [];
  orphanCultures: CultureGroup[] = [];
  showZoneForm = false;

  // Édition / suppression de zone (slide-in du formulaire + dialogue de confirmation).
  showZoneEdit = false;
  editZone?: any;
  private editZoneView?: ZoneView;
  showZoneDeleteConfirm = false;
  deletingZone = false;
  zoneDeleteCount = 0;
  private deleteTargetUid?: string;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private storageService: StorageService,
    private locationApi: LocationApi,
    private gardenBedApi: GardenBedApi,
    private plantApi: PlantApi,
    private cultivationHierarchyApi: CultivationHierarchyApi,
  ) {}

  async ngOnInit(): Promise<void> {
    // Le toast « + » → « Ajouter une zone » navigue ici avec ?addZone=1 pour ouvrir le formulaire.
    this.route.queryParamMap.subscribe((params) => {
      this.showZoneForm = params.get('addZone') === '1';
    });
    await this.reload();
  }

  private async reload(): Promise<void> {
    this.isLoading = true;
    try {
      await this.loadGarden();
    } finally {
      this.isLoading = false;
    }
  }

  /** Fermeture du formulaire de zone : on efface le signal d'URL et on recharge si une zone a été créée. */
  async onZoneFormClosed(location?: any): Promise<void> {
    this.showZoneForm = false;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { addZone: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    if (location) {
      await this.reload();
    }
  }

  private async loadGarden(): Promise<void> {
    const lang = this.storageService.getCurrentLanguage();
    const addressUid = this.storageService.getItem('address-uid') ?? undefined;

    const [zonesRes, bedsRes, plantsRes, hierarchies] = await Promise.all([
      this.locationApi.list({ addressUid } as any, 0, 50, lang),
      this.gardenBedApi.list({} as any, 0, 200, lang),
      this.plantApi.list({} as any, 0, 500, lang),
      this.cultivationHierarchyApi.myGarden(lang),
    ]);

    const zones = zonesRes.results ?? [];
    const beds = bedsRes.results ?? [];
    const plants = plantsRes.results ?? [];

    // Carte cultivationUid → culture « riche » (image + nom court) issue de myGarden.
    const cultureByUid = new Map<string, any>();
    for (const h of (hierarchies as any[]) ?? []) {
      for (const c of h.cultivations ?? []) cultureByUid.set(c.uid, c);
    }
    const culturesByBed = new Map<string, any[]>();
    const occupiedByBed = new Map<string, { x: number; y: number }[]>();
    const plantCountByBed = new Map<string, number>();
    const actionNeededByBed = new Map<string, boolean>();
    const looseByZone = new Map<string, PlantListResponse[]>();
    const orphanPlants: PlantListResponse[] = [];
    const zoneUids = new Set(zones.map((z) => z.uid));

    for (const p of plants) {
      const bedUid = (p as any).gardenBedUid;
      if (bedUid) {
        this.push(
          culturesByBed,
          bedUid,
          cultureByUid.get((p as any).cultivationUid) ?? p.cultivationVariant,
        );
        // Capacité comptée en plants : on cumule nbPlants (cohorte), pas le nombre d'enregistrements.
        plantCountByBed.set(
          bedUid,
          (plantCountByBed.get(bedUid) ?? 0) + ((p as any).nbPlants ?? 1),
        );
        if (this.needsAction(p)) actionNeededByBed.set(bedUid, true);
        const x = (p as any).positionX;
        const y = (p as any).positionY;
        if (x != null && y != null) this.push(occupiedByBed, bedUid, { x, y });
      } else if (p.location?.uid && zoneUids.has(p.location.uid)) {
        this.push(looseByZone, p.location.uid, p);
      } else {
        orphanPlants.push(p);
      }
    }

    this.orphanCultures = this.groupByCultivation(orphanPlants, cultureByUid);

    const bedsByZone = new Map<string, GardenBedListResponse[]>();
    for (const b of beds) if (b.locationUid) this.push(bedsByZone, b.locationUid, b);

    this.zones = zones.map((zone) => ({
      zone,
      beds: (bedsByZone.get(zone.uid) ?? []).map((bed) => {
        const cultures = culturesByBed.get(bed.uid) ?? [];
        const plantCount = plantCountByBed.get(bed.uid) ?? 0;
        return {
          bed,
          cultures,
          count: cultures.length,
          plantCount,
          freeCells: this.freeCells(bed, plantCount),
          occupied: occupiedByBed.get(bed.uid) ?? [],
          actionNeeded: actionNeededByBed.get(bed.uid) ?? false,
        };
      }),
      looseCultures: this.groupByCultivation(
        looseByZone.get(zone.uid) ?? [],
        cultureByUid,
      ),
    }));
  }

  /** Regroupe des plantes par culture (comme l'accueil) : une entrée par culture + son nombre de plants. */
  private groupByCultivation(
    plants: PlantListResponse[],
    cultureByUid: Map<string, any>,
  ): CultureGroup[] {
    const byKey = new Map<string, CultureGroup>();
    for (const p of plants) {
      const cultivationUid = (p as any).cultivationUid;
      const key = cultivationUid ?? p.cultivationVariant?.uid ?? p.uid;
      let group = byKey.get(key);
      if (!group) {
        group = {
          culture: cultureByUid.get(cultivationUid) ?? p.cultivationVariant,
          cultivationUid,
          count: 0,
          plants: [],
          actionNeeded: false,
        };
        byKey.set(key, group);
      }
      group.plants.push(p);
      group.count++;
      if (this.needsAction(p)) group.actionNeeded = true;
    }
    return Array.from(byKey.values());
  }

  private push<T>(map: Map<string, T[]>, key: string, value: T): void {
    const list = map.get(key) ?? [];
    list.push(value);
    map.set(key, list);
  }

  /** Une plante « attend une action » : étape manuelle (isBlocking) dont la durée est écoulée. */
  private needsAction(p: any): boolean {
    return (
      !!p?.currentGrowingStep?.isBlocking &&
      (p?.currentGrowingStepProgression ?? 0) >= 1
    );
  }

  private freeCells(bed: GardenBedListResponse, used: number): number {
    const total =
      bed.type === GardenBedType.POT
        ? bed.capacity ?? 0
        : (bed.rows ?? 0) * (bed.columns ?? 0);
    return Math.max(0, total - used);
  }

  openBed(bed: GardenBedListResponse): void {
    this.router.navigate(['/garden/beds', bed.uid]);
  }

  /** Ouvre une culture hors planche regroupée (tap sur la vignette) → liste de ses plants, comme l'accueil. */
  openCulture(group: CultureGroup): void {
    if (group?.cultivationUid) {
      this.router.navigate(['/garden/cultivations', group.cultivationUid, 'plants']);
    } else {
      this.openPlant(group?.plants?.[0]);
    }
  }

  /** Ouvre le détail d'une plante précise (fallback quand la culture n'a pas d'uid de cultivation). */
  openPlant(plant: any): void {
    const cultivationUid = plant?.cultivationUid;
    const variantUid = plant?.cultivationVariant?.uid;
    if (cultivationUid && variantUid && plant?.uid) {
      this.router.navigate([
        '/garden/cultivations',
        cultivationUid,
        'variants',
        variantUid,
        'plants',
        plant.uid,
      ]);
    }
  }

  addBedToZone(zone: LocationListResponse): void {
    this.router.navigate(['/garden/beds/create'], {
      queryParams: { locationUid: zone.uid },
    });
  }

  /** Emoji illustratif du type de zone (aligné sur le sélecteur de type). */
  zoneEmoji(type?: string): string {
    switch (type) {
      case 'GREENHOUSE':
        return '🏡';
      case 'BALCONY':
        return '🪟';
      case 'TERRACE':
        return '☀️';
      case 'INDOOR':
        return '🛋️';
      case 'PLANTER_BOX':
        return '🪴';
      case 'GARDEN':
      default:
        return '🌱';
    }
  }

  // --- Édition / suppression d'une zone (point d'entrée : tap sur l'en-tête de zone) ---
  openZoneEdit(z: ZoneView): void {
    this.editZoneView = z;
    this.editZone = z.zone;
    this.showZoneEdit = true;
  }

  onZoneEditClosed(location?: any): void {
    this.showZoneEdit = false;
    this.editZone = undefined;
    if (location) {
      this.reload();
    }
  }

  onZoneDeleteRequest(): void {
    const z = this.editZoneView;
    this.showZoneEdit = false;
    if (!z) return;
    this.deleteTargetUid = z.zone.uid;
    this.zoneDeleteCount = this.zonePlantCount(z);
    this.showZoneDeleteConfirm = true;
  }

  cancelZoneDelete(): void {
    if (this.deletingZone) return;
    this.showZoneDeleteConfirm = false;
  }

  async confirmZoneDelete(): Promise<void> {
    if (!this.deleteTargetUid) return;
    this.deletingZone = true;
    try {
      await this.locationApi.deleteById(
        this.deleteTargetUid,
        this.storageService.getCurrentLanguage(),
      );
      this.showZoneDeleteConfirm = false;
      await this.reload();
    } finally {
      this.deletingZone = false;
    }
  }

  /** Nombre de plants actifs d'une zone (toutes planches + hors planche). */
  zonePlantCount(z: ZoneView): number {
    const inBeds = z.beds.reduce((sum, b) => sum + (b.count || 0), 0);
    const loose = (z.looseCultures || []).reduce((sum, g) => sum + (g.count || 0), 0);
    return inBeds + loose;
  }
}
